import { useEffect, useRef, useState } from 'react';
import { Virtuoso, type VirtuosoHandle } from 'react-virtuoso';

import type { VirtualizedListProps } from '@/types';

import ListLoader from '@/components/shared/loader/list-loader';
import LoadingSpinner from '@/components/shared/loading-spinner';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

const VirtualizedList = <T,>({
  data,
  renderItem,
  isLoading = false,
  isLoadingMore = false,
  hasMore = false,
  isError,
  loadNextPage,
  memoryKey,
  resetDependencies = [],
  emptyView,
  errorView,
}: VirtualizedListProps<T>) => {
  const isInfinite = !!loadNextPage;
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const prevDeps = useRef(resetDependencies);

  const prevMemoryKey = useRef<string | null | undefined>(memoryKey);

  const [scrollParent, setScrollParent] = useState<HTMLElement | null>(null);

  const [targetScrollIndex, setTargetScrollIndex] = useState(() => {
    if (!memoryKey || typeof window === 'undefined') {
      return 0;
    }
    const saved = sessionStorage.getItem(memoryKey);

    return saved ? parseInt(saved, 10) : 0;
  });

  const [isCatchingUp, setIsCatchingUp] = useState(isInfinite && targetScrollIndex > 0);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (!memoryKey) {
      if (prevMemoryKey.current) {
        sessionStorage.removeItem(prevMemoryKey.current);
      }
      setTargetScrollIndex(0);
      setIsCatchingUp(false);
      virtuosoRef.current?.scrollToIndex({ index: 0, behavior: 'smooth' });
    } else if (memoryKey !== prevMemoryKey.current) {
      const saved = sessionStorage.getItem(memoryKey);
      const newTargetIndex = saved ? parseInt(saved, 10) : 0;

      setTargetScrollIndex(newTargetIndex);

      if (newTargetIndex > 0 && isInfinite) {
        setIsCatchingUp(true);
      }
      virtuosoRef.current?.scrollToIndex({
        index: newTargetIndex,
        align: 'start',
        behavior: 'auto',
      });
    }

    const activeKey = memoryKey;
    prevMemoryKey.current = memoryKey;

    return () => {
      if (activeKey) {
        setTimeout(() => {
          if (typeof window !== 'undefined') {
            if (!window.location.href.includes(activeKey)) {
              sessionStorage.removeItem(activeKey);
            }
          }
        }, 0);
      }
    };
  }, [memoryKey, isInfinite]);

  useEffect(() => {
    if (!memoryKey || typeof window === 'undefined') {
      return;
    }
    const handleBeforeUnload = () => sessionStorage.removeItem(memoryKey);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [memoryKey]);

  useEffect(() => {
    const filtersChanged =
      resetDependencies.length !== prevDeps.current.length ||
      resetDependencies.some((dep, i) => dep !== prevDeps.current[i]);

    if (filtersChanged) {
      prevDeps.current = resetDependencies;
      setTargetScrollIndex(0);
      setIsCatchingUp(false);

      if (memoryKey && typeof window !== 'undefined') {
        sessionStorage.setItem(memoryKey, '0');
      }

      virtuosoRef.current?.scrollToIndex({
        index: 0,
        align: 'start',
        behavior: 'smooth',
      });
    }
  }, [resetDependencies, memoryKey, scrollParent]);

  useEffect(() => {
    if (!isInfinite || !isCatchingUp || isLoadingMore || isLoading) {
      return;
    }

    if (data.length > 0) {
      if (data.length <= targetScrollIndex && hasMore && loadNextPage) {
        loadNextPage();
      } else {
        setIsCatchingUp(false);
      }
    }
  }, [
    data.length,
    isCatchingUp,
    targetScrollIndex,
    hasMore,
    isLoadingMore,
    isLoading,
    loadNextPage,
    isInfinite,
  ]);

  useEffect(() => {
    if (wrapperRef.current) {
      requestAnimationFrame(() => {
        const viewport = wrapperRef.current?.querySelector<HTMLElement>(
          '[data-radix-scroll-area-viewport]'
        );

        if (viewport) {
          setScrollParent(viewport);
        }
      });
    }
  }, [data.length, isCatchingUp]);

  if ((isLoading && data.length === 0) || isCatchingUp) {
    return (
      <div className="flex flex-col h-full px-4 w-full">
        <div className="flex-1 flex flex-col justify-center gap-4 w-full">
          <ListLoader />
        </div>
      </div>
    );
  }

  if (isError) {
    return <>{errorView}</>;
  }

  if (!isLoading && data.length === 0 && emptyView) {
    return <>{emptyView}</>;
  }

  const renderVirtualList = () => {
    if (!scrollParent) {
      return null;
    }

    return (
      <Virtuoso
        ref={virtuosoRef}
        data={data}
        customScrollParent={scrollParent}
        initialTopMostItemIndex={targetScrollIndex}
        rangeChanged={(range) => {
          if (memoryKey && typeof window !== 'undefined' && memoryKey === prevMemoryKey.current) {
            sessionStorage.setItem(memoryKey, range.startIndex.toString());
          }
        }}
        increaseViewportBy={{ top: 0, bottom: 400 }}
        endReached={() => {
          if (isInfinite && hasMore && !isLoadingMore && loadNextPage) {
            loadNextPage();
          }
        }}
        itemContent={(index, item) => (
          <div
            className={cn('mb-2', {
              'border-b border-border': index < data.length - 1,
            })}
          >
            {renderItem(item, index)}
          </div>
        )}
        components={
          isInfinite
            ? {
                Footer: () =>
                  isLoadingMore ? (
                    <div className="flex justify-center items-center w-full h-20">
                      <LoadingSpinner />
                    </div>
                  ) : (
                    <div className="h-1 w-full" />
                  ),
              }
            : {}
        }
      />
    );
  };

  return (
    <ScrollArea className="flex-1 min-h-0 w-full" ref={wrapperRef}>
      <div className="px-4">{renderVirtualList()}</div>
    </ScrollArea>
  );
};

export default VirtualizedList;
